import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { productService } from '../../services/productService';
import { contentService } from '../../services/contentService';
import ProductCard from '../../components/ProductCard/ProductCard';
import TopCategoryStory from '../../components/TopCategoryStory/TopCategoryStory';
import TrustStrip from '../../components/TrustStrip/TrustStrip';
import {
  HeroSection,
  SpecialDealsSection,
  ReviewsSection,
  StorySection,
  CommunitySection,
  WelcomeSection
} from './HomeSections';
import './Home.css';

const Home = () => {
  const [newArrivals, setNewArrivals] = useState([]);
  const [bestsellers, setBestsellers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  // Sections edited in Admin → Customize (a section is null when turned off)
  const [content, setContent] = useState(null);
  const [contentLoading, setContentLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    const loadHomeData = async () => {
      try {
        setLoading(true);
        const [allProducts, cats] = await Promise.all([
          productService.getProducts(),
          productService.getCategories()
        ]);
        setNewArrivals(allProducts.filter(p => p.isNewArrival).slice(0, 4));
        setBestsellers(allProducts.filter(p => p.isBestseller).slice(0, 4));
        setCategories(cats);
      } catch (err) {
        console.error("Failed to load home data", err);
      } finally {
        setLoading(false);
      }
    };
    loadHomeData();

    contentService.getHomeContent()
      .then(setContent)
      .catch(err => console.error('Failed to load home page content', err))
      .finally(() => setContentLoading(false));
  }, []);

  return (
    <div className="home-page">
      <h1 className="sr-only">ZIDA – The Feminine Feel | Kurtas, Kurta Sets &amp; Ethnic Wear for Women</h1>

      {/* 1. Signature Top Category Story & 10 Lakh+ Trust Ribbon */}
      <TopCategoryStory content={content?.categoryStrip} />

      {/* 2. Hero Section - carousel managed in Admin → Customize → Hero Banner */}
      <HeroSection content={content?.hero} loading={contentLoading} />

      {/* 3. Promises strip — Admin → Customize → Trust Strip */}
      <TrustStrip content={content?.trustStrip} />

      {/* 4. Shop By Category */}
      <section className="home-section categories-section" aria-labelledby="shop-by-category-title">
        <div className="home-container">
          <div className="section-header text-center">
            <span className="section-kicker">CURATED BY SILHOUETTE</span>
            <h2 id="shop-by-category-title" className="section-title">
              <span>SHOP BY</span> <strong>CATEGORY</strong>
            </h2>
            <p className="section-subtitle">Discover handcrafted ethnic wear crafted from breathable natural fibers</p>
          </div>

          <div className="categories-grid">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/collections/${cat.slug}`}
                className="category-card"
              >
                <div className="category-image-wrap">
                  <img
                    src={cat.image}
                    alt={cat.name}
                    className="category-img"
                    loading="lazy"
                  />
                  <div className="category-overlay"></div>
                </div>
                <div className="category-card-content">
                  <h3 className="category-card-title">{cat.name}</h3>
                  <span className="category-count">{cat.itemCount}</span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 5. New Arrivals */}
      <section className="home-section new-arrivals-section" aria-labelledby="new-arrivals-title">
        <div className="home-container">
          <div className="section-header flex-header">
            <div>
              <span className="section-kicker">FRESH OFF THE LOOM</span>
              <h2 id="new-arrivals-title" className="section-title">
                <span>NEW</span> <strong>ARRIVALS</strong>
              </h2>
              <p className="section-subtitle">Weekly additions of comfortable Jaipur handblock and pastel weaves</p>
            </div>
            <Link to="/collections/new-arrivals" className="view-all-link">
              <span>View All New Styles</span>
              <ArrowRight size={16} />
            </Link>
          </div>

          {loading ? (
            <div className="product-skeleton-grid">
              {[1, 2, 3, 4].map((n) => (
                <div key={n} className="product-skeleton-card"></div>
              ))}
            </div>
          ) : (
            <div className="product-grid">
              {newArrivals.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* 6. Special Deals - Admin → Customize → Special Deals */}
      <SpecialDealsSection content={content?.specialDeals} />

      {/* 7. Bestsellers */}
      <section className="home-section bestsellers-section" aria-labelledby="bestsellers-title">
        <div className="home-container">
          <div className="section-header flex-header">
            <div>
              <span className="section-kicker">CUSTOMER FAVORITES</span>
              <h2 id="bestsellers-title" className="section-title">
                <span>MOST LOVED</span> <strong>KURTAS &amp; SETS</strong>
              </h2>
              <p className="section-subtitle">Loved for their breathable cotton touch, deep side pockets, and flattering fit</p>
            </div>
            <Link to="/collections/kurtas" className="view-all-link">
              <span>View All Bestsellers</span>
              <ArrowRight size={16} />
            </Link>
          </div>

          {loading ? (
            <div className="product-skeleton-grid">
              {[1, 2, 3, 4].map((n) => (
                <div key={n} className="product-skeleton-card"></div>
              ))}
            </div>
          ) : (
            <div className="product-grid">
              {bestsellers.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* 8. Customer Reviews - Admin → Customize → Reviews */}
      <ReviewsSection content={content?.reviews} />

      {/* 9. Brand Story - Admin → Customize → Brand Story */}
      <StorySection content={content?.story} />

      {/* 10. Community Gallery - Admin → Customize → Community Gallery */}
      <CommunitySection content={content?.community} />

      {/* 11. Welcome Offer / Newsletter - Admin → Customize → Welcome Offer */}
      <WelcomeSection content={content?.welcome} />
    </div>
  );
};

export default Home;
